import { TabsWithRoutes } from "@components/common/tabs/tabs-with-routes";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { MemberOverviewPage } from "@pages/profile/member-overview-page";
import { MemberTabs } from "@router/route-params";
import { generateTeamDetailsRoute, useAppParams } from "@router/router-helper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { NotFoundPage } from "@pages/not-found-page";
import { fetchCompanyMemberDetails } from "@store/members/members-slice-thunk";
import { useEffect } from "react";
import { fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { companyMemberSelector } from "@store/members/members-selector";
import { isTeam } from "@utils/team-utils";
import { Navigate } from "react-router-dom";
import { resetCompanyMemberGroupsState } from "@store/company-member-groups/company-member-groups-slice";
import { OpenTabEvents } from "@utils/track-event/track-event-list";

/**
 * Content in the member page
 */
export function MemberPage(): JSX.Element {
  const { memberTabs, memberId } = useAppParams();
  const companyId = useCompanyId();
  const dispatch = useAppDispatch();
  const companyMember = useAppSelector(companyMemberSelector(memberId));
  const { isFetchingCompanyMemberDetails } = useAppSelector(
    fetchingMembersFlagsSelector
  );

  // Fetches the member details
  useEffect(() => {
    if (memberId) {
      void dispatch(
        fetchCompanyMemberDetails({
          companyId,
          identity: memberId,
        })
      );
    }

    return () => {
      /* Clean the store for the company members group */
      dispatch(resetCompanyMemberGroupsState());
    };
  }, [companyId, dispatch, memberId]);

  // Show not found page if member tab is not valid
  if (!memberTabs || !Object.values(MemberTabs).includes(memberTabs)) {
    return <NotFoundPage />;
  }

  // Show not found page if there is not a valid member object after fetching is complete
  if (!companyMember && !isFetchingCompanyMemberDetails) {
    return <NotFoundPage />;
  }

  // Redirect to team details page if the member is a team
  if (companyMember && isTeam(companyMember)) {
    return (
      <Navigate
        to={generateTeamDetailsRoute(companyId, companyMember.identity)}
        replace
      />
    );
  }

  return (
    <TabsWithRoutes
      selectedTab={memberTabs}
      dataTestId="MEMBER_PAGE_CONTAINER"
      trackingEventPageName={OpenTabEvents.openMemberAccountTab}
      requiredAttribute={companyMember}
      tabs={[
        {
          label: "Overview",
          route: MemberTabs.overview,
          content: (user) => (
            <MemberOverviewPage
              member={user}
              isLoading={isFetchingCompanyMemberDetails}
            />
          ),
          loadingContent: <MemberOverviewPage member={null} isLoading={true} />,
        },
      ]}
    />
  );
}
